/**
 * Autocop Design System tokens
 * Source: apps/front/.design-source/autocop/project/design-system/tokens.jsx
 *
 * Two modes: light (default) and dark. Naming is flat & semantic
 * (surface.canvas, border.subtle, text.primary, brand.primary, ...).
 *
 * Light is the default mode — dark is an explicit opt-in via
 * `data-color-mode="dark"` on <html>.
 */

export type ColorMode = 'light' | 'dark';

type DsColorTokens = {
  // Surfaces
  'surface.canvas': string;
  'surface.default': string;
  'surface.muted': string;
  'surface.subtle': string;
  'surface.sunken': string;
  'surface.elevated': string;
  // Borders
  'border.subtle': string;
  'border.default': string;
  'border.strong': string;
  'border.focus': string;
  // Text
  'text.primary': string;
  'text.secondary': string;
  'text.tertiary': string;
  'text.inverse': string;
  'text.brand': string;
  // Brand / primary
  'brand.primary': string;
  'brand.primaryHover': string;
  'brand.primarySoft': string;
  // CTA (neutral action — black in light, white in dark)
  'cta.default': string;
  'cta.hover': string;
  'cta.foreground': string;
  // Semantic
  'success.default': string;
  'success.soft': string;
  'success.border': string;
  'error.default': string;
  'error.soft': string;
  'error.border': string;
  'warning.default': string;
  'warning.soft': string;
  'warning.border': string;
  'info.default': string;
  'info.soft': string;
  'info.border': string;
  // Shadows
  'shadow.xs': string;
  'shadow.sm': string;
  'shadow.md': string;
  'shadow.lg': string;
  'shadow.primary': string;
};

export const dsTokens: Record<ColorMode, DsColorTokens> = {
  light: {
    'surface.canvas':      '#E5E5E5',
    'surface.default':     '#FFFFFF',
    'surface.muted':       '#F5F5F5',
    'surface.subtle':      '#FAFAFA',
    'surface.sunken':      '#F4F4F5',
    'surface.elevated':    '#FFFFFF',
    'border.subtle':       '#F0F0F0',
    'border.default':      '#EAEAEA',
    'border.strong':       '#D4D4D8',
    'border.focus':        '#3B82F6',
    'text.primary':        '#0A0A0A',
    'text.secondary':      '#52525B',
    'text.tertiary':       '#A1A1AA',
    'text.inverse':        '#FFFFFF',
    'text.brand':          '#3B82F6',
    'brand.primary':       '#3B82F6',
    'brand.primaryHover':  '#2563EB',
    'brand.primarySoft':   '#EFF6FF',
    'cta.default':         '#0A0A0A',
    'cta.hover':           '#27272A',
    'cta.foreground':      '#FFFFFF',
    'success.default':     '#16A34A',
    'success.soft':        '#DCFCE7',
    'success.border':      '#D1FAE5',
    'error.default':       '#DC2626',
    'error.soft':          '#FEE2E2',
    'error.border':        '#FEE2E2',
    'warning.default':     '#D97706',
    'warning.soft':        '#FEF3C7',
    'warning.border':      '#FDE68A',
    'info.default':        '#2563EB',
    'info.soft':           '#DBEAFE',
    'info.border':         '#DBEAFE',
    'shadow.xs':           '0 1px 1px rgba(0,0,0,0.03)',
    'shadow.sm':           '0 1px 2px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
    'shadow.md':           '0 4px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
    'shadow.lg':           '0 12px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)',
    'shadow.primary':      '0 1px 2px rgba(59,130,246,0.25), 0 0 0 1px rgba(59,130,246,0.5)',
  },
  dark: {
    'surface.canvas':      '#0A0A0C',
    'surface.default':     '#111114',
    'surface.muted':       '#0E0E11',
    'surface.subtle':      '#16161A',
    'surface.sunken':      '#1F1F24',
    'surface.elevated':    '#18181B',
    'border.subtle':       '#18181C',
    'border.default':      '#1F1F25',
    'border.strong':       '#2D2D35',
    'border.focus':        '#818CF8',
    'text.primary':        '#F4F4F5',
    'text.secondary':      '#A1A1AA',
    'text.tertiary':       '#71717A',
    'text.inverse':        '#0A0A0C',
    'text.brand':          '#818CF8',
    'brand.primary':       '#818CF8',
    'brand.primaryHover':  '#6366F1',
    'brand.primarySoft':   'rgba(129,140,248,0.14)',
    'cta.default':         '#F4F4F5',
    'cta.hover':           '#E4E4E7',
    'cta.foreground':      '#0A0A0C',
    'success.default':     '#22C55E',
    'success.soft':        'rgba(34,197,94,0.10)',
    'success.border':      'rgba(34,197,94,0.20)',
    'error.default':       '#EF4444',
    'error.soft':          'rgba(239,68,68,0.10)',
    'error.border':        'rgba(239,68,68,0.20)',
    'warning.default':     '#F59E0B',
    'warning.soft':        'rgba(245,158,11,0.10)',
    'warning.border':      'rgba(245,158,11,0.20)',
    'info.default':        '#3B82F6',
    'info.soft':           'rgba(59,130,246,0.10)',
    'info.border':         'rgba(59,130,246,0.20)',
    'shadow.xs':           'none',
    'shadow.sm':           'none',
    'shadow.md':           '0 4px 12px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)',
    'shadow.lg':           '0 12px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)',
    'shadow.primary':      '0 0 0 3px rgba(129,140,248,0.18)',
  },
};

export const dsType = {
  families: {
    sans: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  scale: {
    display: { px: 32,   lh: 1.1,  weight: 700 },
    h1:      { px: 24,   lh: 1.2,  weight: 700 },
    h2:      { px: 20,   lh: 1.3,  weight: 600 },
    h3:      { px: 16,   lh: 1.4,  weight: 600 },
    body:    { px: 14,   lh: 1.5,  weight: 400 },
    bodySm:  { px: 13,   lh: 1.45, weight: 400 },
    label:   { px: 12,   lh: 1.4,  weight: 500 },
    caption: { px: 11,   lh: 1.4,  weight: 500 },
    micro:   { px: 10.5, lh: 1.3,  weight: 500 },
  },
} as const;

export const dsSpacing = {
  '0':  0,
  '1':  2,
  '2':  4,
  '3':  6,
  '4':  8,
  '5':  10,
  '6':  12,
  '7':  14,
  '8':  16,
  '10': 20,
  '12': 24,
  '16': 32,
  '20': 40,
  '24': 48,
} as const;

export const dsRadius = {
  none: 0,
  xs:   3,
  sm:   5,
  md:   7,
  lg:   9,
  xl:   12,
  '2xl': 14,
  '3xl': 16,
  full: 999,
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
export const pxToRem = (px: number) => `${px / 16}rem`;

// ---------------------------------------------------------------------------
// Compat shim — kept so the AntD-era components (Button, Input, Tag, Modal
// usages, panda.config legacy exports, etc.) keep building during the
// AntD coexistence period (Phase 1 → Phase 5). Removed in Phase 5.
// ---------------------------------------------------------------------------
export const accentColors = {
  primary:       dsTokens.light['brand.primary'],
  primaryHover:  dsTokens.light['brand.primaryHover'],
  primaryActive: dsTokens.light['brand.primaryHover'],
  primaryDark:   dsTokens.dark['brand.primary'],
  orange:        dsTokens.light['warning.default'],
  red:           dsTokens.light['error.default'],
  green:         dsTokens.light['success.default'],
  blue:          dsTokens.light['info.default'],
} as const;

export const baseColors = {
  bg:         dsTokens.light['surface.canvas'],
  segBg:      dsTokens.light['surface.subtle'],
  elevatedBg: dsTokens.light['surface.elevated'],
  selectedBg: dsTokens.light['surface.sunken'],
  border:     dsTokens.light['border.default'],
} as const;

export const fontColors = {
  primary:   dsTokens.light['text.primary'],
  secondary: dsTokens.light['text.secondary'],
  muted:     dsTokens.light['text.tertiary'],
  lowkey:    dsTokens.light['text.tertiary'],
} as const;

export const fontSizes = {
  '2xs': 10,
  xs:    11,
  sm:    12,
  md:    13,
  lg:    15,
  xl:    18,
} as const;

export const spacing = {
  xxs: 2,
  xs:  4,
  sm:  8,
  md:  16,
  lg:  24,
} as const;

export const radii = {
  xs: 4,
  sm: 6,
  lg: 10,
  xl: 99,
} as const;

export const semanticColors = {
  bg: {
    default:  baseColors.bg,
    surface:  baseColors.segBg,
    elevated: baseColors.selectedBg,
    hover:    'rgba(59,130,246,0.10)',
    active:   'rgba(59,130,246,0.20)',
    selected: baseColors.selectedBg,
    primary:  accentColors.primary,
    orange:   accentColors.orange,
    red:      accentColors.red,
    green:    accentColors.green,
    blue:     accentColors.blue,
  },
  text: {
    primary:   fontColors.primary,
    secondary: fontColors.secondary,
    muted:     fontColors.muted,
    lowkey:    fontColors.lowkey,
    inverse:   baseColors.bg,
    accent:    accentColors.primary,
    orange:    accentColors.orange,
  },
  border: {
    default: baseColors.border,
    subtle:  dsTokens.light['border.subtle'],
    accent:  accentColors.primary,
    hover:   'rgba(59,130,246,0.50)',
  },
} as const;

export const componentTokens = {
  sidebar: {
    bg:       baseColors.segBg,
    selected: baseColors.selectedBg,
    hover:    'rgba(59,130,246,0.10)',
  },
  card: {
    bg:     baseColors.segBg,
    border: baseColors.border,
    hover:  'rgba(59,130,246,0.05)',
  },
  button: {
    primary: {
      bg:     accentColors.primary,
      hover:  accentColors.primaryHover,
      active: accentColors.primaryActive,
      text:   '#ffffff',
    },
    secondary: {
      bg:     baseColors.segBg,
      hover:  baseColors.selectedBg,
      active: baseColors.selectedBg,
      text:   fontColors.primary,
      border: baseColors.border,
    },
    outline: {
      bg:     'transparent',
      hover:  'rgba(59,130,246,0.10)',
      active: 'rgba(59,130,246,0.20)',
      text:   accentColors.primary,
      border: accentColors.primary,
    },
  },
} as const;

export const remValues = {
  fontSizes: {
    '2xs': pxToRem(fontSizes['2xs']),
    xs:    pxToRem(fontSizes.xs),
    sm:    pxToRem(fontSizes.sm),
    md:    pxToRem(fontSizes.md),
    lg:    pxToRem(fontSizes.lg),
    xl:    pxToRem(fontSizes.xl),
  },
  spacing: {
    xxs: pxToRem(spacing.xxs),
    xs:  pxToRem(spacing.xs),
    sm:  pxToRem(spacing.sm),
    md:  pxToRem(spacing.md),
    lg:  pxToRem(spacing.lg),
  },
  radii: {
    xs: pxToRem(radii.xs),
    sm: pxToRem(radii.sm),
    lg: pxToRem(radii.lg),
    xl: pxToRem(radii.xl),
  },
} as const;

export type BaseColor = keyof typeof baseColors;
export type FontColor = keyof typeof fontColors;
export type AccentColor = keyof typeof accentColors;
export type FontSize = keyof typeof fontSizes;
export type Spacing = keyof typeof spacing;
export type Radii = keyof typeof radii;
